import { useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import { TUpdateAgentPreferenceBodySchema } from '@cloud-wave/neon-common-lib';
import { getDateFormat } from '@cloud-wave/neon-common-lib/app';

import { useConfigContext } from 'lib/core/config';
import { useAuthContext } from 'lib/core/context/AuthProvider';

import Context from 'lib/common/contexts/AgentPreferenceContext/Context';
import { updateAgentPreference } from 'lib/common/contexts/AgentPreferenceContext/api/updateAgentPreference';
import { validateAgentAddress } from 'lib/common/contexts/AgentPreferenceContext/api/validateAgentAddress';

import toast from 'lib/common/utils/toast';

import agentDefaultPreference from './constants/agentDefaultPreference';

/** This context is only for global preferences that are needed by multiple components.
 * Other agent preferences can be found in the preferences overlay **/
function AgentPreferencesProvider({ children }) {
  const { fetch_, userPreferences: initialUserPreferences } = useAuthContext();
  const { config } = useConfigContext();
  const preferredDateFormat = getDateFormat();
  const [userPreferences, setUserPreferences] = useState(initialUserPreferences || agentDefaultPreference);

  const [preferenceModalOpen, setPreferenceModalOpen] = useState(false);

  const { mutateAsync: updateAgentPreferenceMutation } = useMutation({
    mutationFn: updateAgentPreference,
    onSuccess: (data) => setUserPreferences(data),
    onError: (e: any) => {
      toast(
        'error',
        e.data?.errors?.[0] || e.data || 'Oops, something went wrong updating your preferences. Please try again.'
      );
    },
  });

  const { mutateAsync: validateAddressMutation } = useMutation({
    mutationFn: validateAgentAddress,
    onError: (e: any) => {
      toast(
        'error',
        e.data?.errors?.[0] || e.data || 'Oops, something went wrong validating your address. Please try again.'
      );
    },
  });

  const validateAddress = (location: TUpdateAgentPreferenceBodySchema['location']) => {
    return validateAddressMutation({
      config,
      fetch_,
      location,
    });
  };

  return (
    <Context.Provider
      value={{
        state: {
          agentPreference: userPreferences,
          preferenceModalOpen,
          preferredDateFormat,
        },
        actions: {
          updatePreference: async (agentPreference: TUpdateAgentPreferenceBodySchema) => {
            await updateAgentPreferenceMutation({
              agentPreference,
              config,
              fetch_,
            });
          },
          validateAddress,
          setPreferenceModalOpen,
        },
      }}
    >
      {children}
    </Context.Provider>
  );
}

export default AgentPreferencesProvider;
